import { useTranslation } from 'react-i18next'

interface RegisterScopeToggleProps {
  activeOnly: boolean
  inFlowCount: number
  totalCount: number
  onChange: (activeOnly: boolean) => void
}

const BUTTON_BASE =
  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4e5e82]'
const BUTTON_SELECTED = 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
const BUTTON_UNSELECTED =
  'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'

/**
 * Register scope control (FR8).
 *
 * The evidence register defaults to items in the custody flow. On a real case
 * most rows are bare registrations — 94 of 103 on the reference case — which
 * buried the handful of items actually in motion.
 *
 * Both counts are always visible so the narrowed view can never be mistaken for
 * the whole register, per the "X of Y total rows" data-scope rule.
 *
 * Composable with the status filter: this narrows the set, the status dropdown
 * selects within it.
 */
export function RegisterScopeToggle({
  activeOnly,
  inFlowCount,
  totalCount,
  onChange,
}: RegisterScopeToggleProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div
      role="group"
      aria-label={t('scope.label')}
      className="inline-flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800"
    >
      <button
        type="button"
        aria-pressed={activeOnly}
        onClick={() => {
          onChange(true)
        }}
        className={`${BUTTON_BASE} ${activeOnly ? BUTTON_SELECTED : BUTTON_UNSELECTED}`}
      >
        {t('scope.inCustodyFlow', { count: inFlowCount })}
      </button>
      <button
        type="button"
        aria-pressed={!activeOnly}
        onClick={() => {
          onChange(false)
        }}
        className={`${BUTTON_BASE} ${activeOnly ? BUTTON_UNSELECTED : BUTTON_SELECTED}`}
      >
        {t('scope.allRegistered', { count: totalCount })}
      </button>
    </div>
  )
}
